import React, { useState, useEffect, useRef } from 'react';
import { Save, Upload, User, Mail, Phone, Shield, Building2, KeyRound, X } from 'lucide-react';
import { FeaturedLayout } from './layouts';
import { useAuth } from '../hooks/useAuth';
import { useOrganization } from '../contexts/OrganizationContext';
import { Button, buttonVariants } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Separator } from './ui/separator';
import { supabase } from '../lib/supabase';

const ROLE_LABELS = {
  platform_admin: 'Administrador de Plataforma',
  admin: 'Administrador',
  head_coach: 'Head Coach',
  manager: 'Manager',
  coach: 'Coach',
  assistant: 'Asistente',
};

const Profile = () => {
  const { user, staff, refreshStaff } = useAuth();
  const { orgName } = useOrganization();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    nombre_completo: '',
    telefono: '',
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Cambio de contrasena
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ new: '', confirm: '' });
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    if (staff) {
      setForm({
        nombre_completo: staff.nombre_completo || '',
        telefono: staff.telefono || '',
      });
    }
  }, [staff]);

  const hasChanges =
    form.nombre_completo !== (staff?.nombre_completo || '') ||
    form.telefono !== (staff?.telefono || '') ||
    !!photoFile;

  const handleChange = (field, value) => {
    setMessage(null);
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'La foto no debe superar 2 MB', ts: Date.now() });
      return;
    }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!staff?.id) return;

    setSaving(true);
    setMessage(null);

    try {
      const updates = {
        nombre_completo: form.nombre_completo.trim(),
        telefono: form.telefono.trim() || null,
      };

      // Subir foto si hay nueva (directo a storage)
      if (photoFile) {
        const ext = photoFile.name.split('.').pop();
        const filePath = `staff/${staff.id}.${ext}`;

        const { error: uploadError } = await supabase.storage
          .from('avatars')
          .upload(filePath, photoFile, { upsert: true });

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(filePath);
        updates.foto_url = `${urlData.publicUrl}?t=${Date.now()}`;
      }

      const { error } = await supabase.from('staff').update(updates).eq('id', staff.id);
      if (error) throw error;

      setMessage({ type: 'success', text: 'Listo. Tu perfil se actualizo.', ts: Date.now() });
      setPhotoFile(null);
      setPhotoPreview(null);
      await refreshStaff();
    } catch (err) {
      console.error('Error updating profile:', err);
      setMessage({ type: 'error', text: `Algo salio mal: ${err.message}`, ts: Date.now() });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.new || passwordForm.new.length < 8) {
      setMessage({ type: 'error', text: 'La contrasena debe tener al menos 8 caracteres', ts: Date.now() });
      return;
    }
    if (passwordForm.new !== passwordForm.confirm) {
      setMessage({ type: 'error', text: 'Las contrasenas no coinciden', ts: Date.now() });
      return;
    }

    setSavingPassword(true);
    setMessage(null);

    try {
      const { error } = await supabase.auth.updateUser({ password: passwordForm.new });
      if (error) throw error;

      setMessage({ type: 'success', text: 'Listo. Tu contrasena se actualizo.', ts: Date.now() });
      setPasswordForm({ new: '', confirm: '' });
      setShowPasswordForm(false);
    } catch (err) {
      console.error('Error updating password:', err);
      setMessage({ type: 'error', text: `Algo salio mal: ${err.message}`, ts: Date.now() });
    } finally {
      setSavingPassword(false);
    }
  };

  if (!staff) {
    return (
      <FeaturedLayout title="Mi perfil">
        <div className="p-6 text-center text-muted-foreground">Cargando...</div>
      </FeaturedLayout>
    );
  }

  const photoUrl = photoPreview || staff.foto_url;
  const initials = (staff.nombre_completo || staff.email || 'U')
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <FeaturedLayout title="Mi perfil">
      <div className="max-w-2xl mx-auto flex flex-col gap-6">
        {message && (
          <div
            key={message.ts}
            className={`rounded-xl p-4 text-sm font-medium animate-in fade-in duration-300 ${
              message.type === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}
          >
            {message.text}
          </div>
        )}

        {/* Foto + Datos basicos */}
        <div className="bg-background rounded-xl border p-6 flex flex-col gap-4">
          <div className="flex items-center gap-2 text-foreground font-semibold text-lg">
            <User />
            Datos personales
          </div>

          <div className="flex items-center gap-6">
            <Avatar className="size-20 rounded-xl">
              {photoUrl && <AvatarImage src={photoUrl} alt={staff.nombre_completo} />}
              <AvatarFallback className="rounded-xl bg-muted text-2xl font-semibold text-muted-foreground">
                {initials}
              </AvatarFallback>
            </Avatar>

            <div className="flex flex-col gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={handlePhotoSelect}
                className="hidden"
              />
              <label className={buttonVariants({ variant: 'secondary', size: 'sm', className: 'cursor-pointer' })}>
                <Upload className="size-4 shrink-0" />
                {photoPreview ? 'Cambiar' : 'Subir foto'}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handlePhotoSelect}
                  className="sr-only"
                />
              </label>
              {photoPreview && (
                <Button
                  variant="link"
                  size="sm"
                  className="text-destructive h-auto p-0 justify-start"
                  onClick={() => {
                    setPhotoFile(null);
                    setPhotoPreview(null);
                  }}
                >
                  <X data-icon="inline-start" />
                  Cancelar
                </Button>
              )}
              <p className="text-xs text-muted-foreground">PNG o JPG, max 2 MB</p>
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="nombre">Nombre completo</Label>
              <Input
                id="nombre"
                value={form.nombre_completo}
                onChange={(e) => handleChange('nombre_completo', e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="telefono">Teléfono</Label>
              <Input
                id="telefono"
                type="tel"
                value={form.telefono}
                onChange={(e) => handleChange('telefono', e.target.value)}
                placeholder="Ej: +52 81 1234 5678"
              />
            </div>
          </div>
        </div>

        {/* Cuenta */}
        <div className="bg-background rounded-xl border p-6 flex flex-col gap-4">
          <div className="flex items-center gap-2 text-foreground font-semibold text-lg">
            <Mail />
            Cuenta
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label>Email</Label>
              <Input value={staff.email || user?.email || ''} disabled />
              <p className="text-xs text-muted-foreground">El email no se puede cambiar desde aqui</p>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Rol</Label>
              <div className="flex h-10 items-center gap-2 rounded-md border border-input bg-muted px-3 text-sm">
                <Shield className="size-4 text-muted-foreground" />
                {ROLE_LABELS[staff.rol] || staff.rol}
              </div>
            </div>
          </div>

          {staff.org_id && (
            <div className="flex flex-col gap-1.5">
              <Label>Organización</Label>
              <div className="flex h-10 items-center gap-2 rounded-md border border-input bg-muted px-3 text-sm">
                <Building2 className="size-4 text-muted-foreground" />
                {orgName}
              </div>
            </div>
          )}
        </div>

        {/* Seguridad */}
        <div className="bg-background rounded-xl border p-6 flex flex-col gap-4">
          <div className="flex items-center gap-2 text-foreground font-semibold text-lg">
            <KeyRound />
            Seguridad
          </div>

          {!showPasswordForm ? (
            <Button variant="outline" onClick={() => setShowPasswordForm(true)} className="self-start">
              <KeyRound data-icon="inline-start" />
              Cambiar contrasena
            </Button>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="new-password">Nueva contrasena</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={passwordForm.new}
                  onChange={(e) => setPasswordForm((prev) => ({ ...prev, new: e.target.value }))}
                  placeholder="Minimo 8 caracteres"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="confirm-password">Confirmar contrasena</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={passwordForm.confirm}
                  onChange={(e) => setPasswordForm((prev) => ({ ...prev, confirm: e.target.value }))}
                />
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={handleChangePassword} disabled={savingPassword}>
                  <Save data-icon="inline-start" />
                  {savingPassword ? 'Guardando...' : 'Actualizar contrasena'}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowPasswordForm(false);
                    setPasswordForm({ new: '', confirm: '' });
                  }}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Guardar */}
        <div className="flex justify-end pb-6">
          <Button size="lg" onClick={handleSave} disabled={saving || !form.nombre_completo.trim() || !hasChanges}>
            <Save data-icon="inline-start" />
            {saving ? 'Guardando...' : !hasChanges ? 'Sin cambios' : 'Guardar cambios'}
          </Button>
        </div>
      </div>
    </FeaturedLayout>
  );
};

export default Profile;
